import React, { useRef, useState, useEffect } from 'react';
import StepLayout from '../components/StepLayout';
import { api } from '../api';
import { isTerminalErrorStatus } from '../utils/recoveryState';
import { ID_BADGE_ENABLED, ID_VERIFICATION_AUTOMATED } from '../utils/featureFlags';
import { isUKSeller } from '../utils/sellerRegion';

const TUTORIAL_URL = 'https://www.linkedin.com/help/linkedin/answer/a1359065';

// How long to wait for the rescrape Celery task before flipping to the
// "not found" retry view. The verify_id_badge primitive typically returns
// in 6-12s; 20s gives us comfortable headroom without making the seller
// stare at a spinner forever.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 20000;

// Legacy "open LinkedIn and add it yourself" verification screen — preserved
// verbatim for the pre-v2 flow (ID_VERIFICATION_AUTOMATED OFF) and its
// regression tests. The v2 automated QR flow lives in Step12Automated below.
function Step12Legacy({ sessionId, sessionData, updateData, onNext }) {
  // 'idle' = initial waiting page, 'verifying' = rescrape in flight,
  // 'notFound' = rescrape finished without seeing the badge (retry view).
  const [mode, setMode] = useState('idle');
  const [error, setError] = useState('');
  const pollAbort = useRef(false);
  const skippedRef = useRef(false);

  // B-IDBADGE-GOTOSTEP (2026-06-02): defense-in-depth self-skip — same as
  // Step09. If the ID-badge sub-flow is disabled, never render the
  // verification ("Waiting for you") screen even if reached via a direct
  // setStep / resume path that bypassed App.shouldSkip. Plain advance — the
  // seller never accepted anything (no updateIDBadge).
  useEffect(() => {
    if (ID_BADGE_ENABLED) return;
    if (skippedRef.current) return;
    skippedRef.current = true;
    onNext();
  }, [onNext]);

  const isGBP = isUKSeller(sessionData);
  const symbol = isGBP ? '£' : '$';

  const verify = async () => {
    setError('');
    setMode('verifying');
    pollAbort.current = false;

    try {
      await api.rescrapeBadge(sessionId);
    } catch (err) {
      setError(err.message || "We couldn't enqueue the badge check. Try again in a few seconds.");
      setMode('idle');
      return;
    }

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline && !pollAbort.current) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      let status;
      try {
        status = await api.getStatus(sessionId);
      } catch (_) {
        continue;
      }
      if (status?.verifiedBadgePresent === true) {
        try { await api.identityConfirm(sessionId); } catch (_) { /* non-fatal */ }
        updateData({
          idBadgeAddedConfirmedAt: new Date().toISOString(),
          verifiedBadgePresent: true,
        });
        onNext();
        return;
      }
    }

    setMode('notFound');
  };

  // B-IDBADGE-NONBLOCK (2026-05-29): the seller must ALWAYS be able to
  // move forward from this step. The in-session rescrape frequently can't
  // confirm the badge — the DICloak profile may already be released by
  // session_cleanup (B-NO-POOL-PICK → rescrape errors, soft-failed to
  // badgeFound=false on the BE), and even on a live profile LinkedIn's
  // async phone/Persona verification does not surface in-session. Without
  // an unconditional exit, the seller is trapped on "Check again" forever
  // and never reaches bank/payment (incident: ce8f8e64 / LI-0219).
  //
  // advance() records the claim (id_badge_accepted) so the operator can
  // verify + credit the £5 bonus out-of-band, then proceeds. The bonus is
  // NOT credited here: it is held until verified_badge_present === true
  // (see utils/earningsProgress.isIdBadgeEarned + Step21/Step22 totals).
  // identity_confirm is idempotent (email-change already auto-chained at
  // login) — a safe no-op.
  const advance = async () => {
    try { await api.updateIDBadge(sessionId, { accepted: true }); } catch (_) { /* non-fatal */ }
    try { await api.identityConfirm(sessionId); } catch (_) { /* non-fatal */ }
    updateData({
      idBadgeAccepted: true,
      idBadgeUpsell: 'Yes',
      idBadgeAddedConfirmedAt: new Date().toISOString(),
    });
    onNext();
  };

  if (!ID_BADGE_ENABLED) return null;

  if (mode === 'notFound') {
    return (
      <StepLayout title={null} hideTrustpilot>
        <div className="waiting-for-you" role="status" aria-live="polite">
          <div className="wfy-nf-icon" aria-hidden="true">
            <span className="wfy-nf-core">
              <svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
                <path d="M6 6 L18 18 M18 6 L6 18" fill="none" stroke="#BA7517" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </span>
          </div>

          <h1 className="wfy-heading">We can&rsquo;t find your badge yet</h1>

          <p className="wfy-instruction wfy-nf-sub">
            It can take a minute to appear on LinkedIn. Wait a moment and try again.
          </p>

          {error && <p className="form-field__error">{error}</p>}

          {/* B-IDBADGE-NONBLOCK (2026-05-29): primary exit. The seller is
              never trapped — "Continue" advances regardless of whether the
              badge could be confirmed in-session. We verify + credit the
              £5 once the badge actually appears on LinkedIn. */}
          <button
            type="button"
            onClick={advance}
            className="primary-cta wfy-confirm"
          >
            Continue
          </button>

          <button
            type="button"
            onClick={verify}
            className="badge-skip-link"
          >
            Check again
          </button>

          <p className="wfy-disclaimer">
            We&rsquo;ll verify your badge on LinkedIn and add your {symbol}5 bonus once it&rsquo;s confirmed.
          </p>

          <p className="wfy-tutorial wfy-nf-tutorial">
            Stuck?{' '}
            <a
              href={TUTORIAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="wfy-tutorial-link"
            >
              Watch the tutorial
            </a>
          </p>
        </div>
      </StepLayout>
    );
  }

  const verifying = mode === 'verifying';

  return (
    <StepLayout title={null} hideTrustpilot>
      <div className="waiting-for-you">
        <div className="wfy-pulse" aria-hidden="true">
          <span className="wfy-pulse-ring" />
          <span className="wfy-pulse-ring wfy-pulse-ring--delayed" />
          <span className="wfy-pulse-core">
            <svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
              <path
                d="M12 2 L20 5 V11 C20 16 16.5 19.5 12 22 C7.5 19.5 4 16 4 11 V5 Z"
                fill="none"
                stroke="#27500A"
                strokeWidth="1.8"
                strokeLinejoin="round"
              />
              <path
                d="M8.5 12 L11 14.5 L15.5 9.5"
                fill="none"
                stroke="#27500A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>

        <h1 className="wfy-heading">Waiting for you</h1>

        <p className="wfy-instruction">
          Open LinkedIn, add your verified badge, then come back and tap the button.
        </p>

        <p className="wfy-tutorial">
          Stuck?{' '}
          <a
            href={TUTORIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="wfy-tutorial-link"
          >
            Watch the tutorial video
          </a>
        </p>

        {error && <p className="form-field__error">{error}</p>}

        <button
          type="button"
          onClick={verify}
          disabled={verifying}
          className="primary-cta wfy-confirm"
        >
          <span className="wfy-confirm-check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M5 12 L10 17 L19 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {verifying ? 'Verifying...' : "I've added my badge"}
        </button>

        {/* B-IDBADGE-NONBLOCK (2026-05-29): always-present escape so the
            seller can proceed without the badge being confirmable
            in-session. Hidden while a verify() poll is in flight to avoid
            a double-advance race. */}
        {!verifying && (
          <button
            type="button"
            onClick={advance}
            className="badge-skip-link"
          >
            Skip for now, I&rsquo;ll add it later
          </button>
        )}

        <p className="wfy-disclaimer">
          We&rsquo;ll verify it on LinkedIn before crediting your {symbol}5 bonus
        </p>
      </div>
    </StepLayout>
  );
}


// ---------------------------------------------------------------------------
// B-IDBADGE-V2 (2026-06-22): automated identity-verification screen.
//
// On entry we POST /id-verification/initiate, which drives LinkedIn's
// verification flow server-side (in the seller's preserved DICloak browser) to
// the QR screen and captures it. We poll /status for idVerificationState:
//   loading  → "Setting up your verification…" spinner
//   qr       → the captured QR (idVerificationQrUrl) + tap-through link +
//              "I've completed it" → POST /id-verification/check
//   checking → "Confirming…" spinner
//   success  → "Submitted — under review" → Continue (bonus held-pending)
//   retry    → "We couldn't confirm it yet" + Check again / Show code / Continue
//   failed   → seller-safe error + Try again / Continue
//
// B-IDBADGE-NONBLOCK preserved: a Continue affordance is ALWAYS present on every
// non-success screen so the seller is never trapped. The £5 bonus is held until
// the badge is actually verified on LinkedIn (operator confirms out-of-band) —
// recording the claim here does NOT credit it.
// ---------------------------------------------------------------------------

const INIT_POLL_INTERVAL_MS = 3000;
// Initiate drives a live browser (open profile → navigate → reach QR) — give it
// generous headroom before declaring failure.
const INIT_POLL_TIMEOUT_MS = 150000;
const CHECK_POLL_INTERVAL_MS = 3000;
const CHECK_POLL_TIMEOUT_MS = 45000;
// Operator directive (2026-06-24): after this many failed verification attempts
// (a check that didn't confirm, or an initiate that failed — "for whatever
// reason"), stop retrying and just continue. The badge is optional and the £5 is
// held until it's actually confirmed on LinkedIn, so auto-continuing costs
// nothing and prevents the badge-step abandonment we saw (e.g. 0d5ccd5b).
const MAX_VERIFY_ATTEMPTS = 3;

function Step12Automated({ sessionId, sessionData, updateData, onNext }) {
  const [phase, setPhase] = useState('loading'); // loading|qr|checking|success|retry|failed|autocontinue
  const [qrUrl, setQrUrl] = useState(sessionData?.idVerificationQrUrl || '');
  const [qrLink, setQrLink] = useState(sessionData?.idVerificationLink || '');
  const [errorMsg, setErrorMsg] = useState('');
  const pollAbort = useRef(false);
  const initiatedRef = useRef(false);
  const advancedRef = useRef(false);
  const attemptsRef = useRef(0);

  const isGBP = isUKSeller(sessionData);
  const symbol = isGBP ? '£' : '$';

  // advance() — record the claim + chain identity-confirm, then move on. Used by
  // both the success path and the always-present Continue. Bonus held-pending.
  const advance = async () => {
    if (advancedRef.current) return;
    advancedRef.current = true;
    pollAbort.current = true;
    try { await api.updateIDBadge(sessionId, { accepted: true }); } catch (_) { /* non-fatal */ }
    try { await api.identityConfirm(sessionId); } catch (_) { /* non-fatal */ }
    updateData({
      idBadgeAccepted: true,
      idBadgeUpsell: 'Yes',
      idBadgeAddedConfirmedAt: new Date().toISOString(),
    });
    onNext();
  };

  // Count a failed verification attempt. After MAX_VERIFY_ATTEMPTS, stop the
  // retry loop entirely and land on 'autocontinue' (which auto-advances) so the
  // seller is never stuck grinding on the optional badge step. Otherwise show
  // the normal retry/failed screen so they can try again if they want.
  const registerFailedAttempt = (fallbackPhase) => {
    attemptsRef.current += 1;
    if (attemptsRef.current >= MAX_VERIFY_ATTEMPTS) {
      setPhase('autocontinue');
      return;
    }
    setPhase(fallbackPhase);
  };

  // Map a /status snapshot to a terminal phase; returns true if it resolved.
  const applyStatus = (status) => {
    const st = status?.idVerificationState;
    if (st === 'verified' || st === 'in_review') {
      updateData({ idVerificationState: st, idBadgeAddedConfirmedAt: new Date().toISOString() });
      setPhase('success');
      return true;
    }
    if (st === 'qr_ready' && status?.idVerificationQrUrl) {
      setQrUrl(status.idVerificationQrUrl);
      setQrLink(status.idVerificationLink || '');
      setPhase('qr');
      return true;
    }
    if (st === 'failed') {
      setErrorMsg(status?.idVerificationError || '');
      setPhase('failed');
      return true;
    }
    return false;
  };

  // Initiate + poll until the QR appears (or a terminal state).
  const startInitiate = async () => {
    pollAbort.current = false;
    setErrorMsg('');
    setPhase('loading');
    try {
      await api.initiateIdVerification(sessionId);
    } catch (_) {
      // enqueue failed — poll anyway; the BE may already have a state. If
      // nothing resolves we hit the timeout → failed.
    }
    const deadline = Date.now() + INIT_POLL_TIMEOUT_MS;
    while (Date.now() < deadline && !pollAbort.current) {
      await new Promise((r) => setTimeout(r, INIT_POLL_INTERVAL_MS));
      let status;
      try { status = await api.getStatus(sessionId); } catch (_) { continue; }
      if (pollAbort.current) return;
      if (isTerminalErrorStatus(status?.status)) {
        setErrorMsg(status?.failureReason || '');
        registerFailedAttempt('failed');
        return;
      }
      if (applyStatus(status)) return;
    }
    if (!pollAbort.current) registerFailedAttempt('failed');
  };

  // Check after the seller says they've completed Persona on their phone.
  const runCheck = async () => {
    pollAbort.current = false;
    setErrorMsg('');
    setPhase('checking');
    try {
      await api.checkIdVerification(sessionId);
    } catch (_) { /* poll anyway */ }
    const deadline = Date.now() + CHECK_POLL_TIMEOUT_MS;
    while (Date.now() < deadline && !pollAbort.current) {
      await new Promise((r) => setTimeout(r, CHECK_POLL_INTERVAL_MS));
      let status;
      try { status = await api.getStatus(sessionId); } catch (_) { continue; }
      if (pollAbort.current) return;
      const st = status?.idVerificationState;
      if (st === 'verified' || st === 'in_review') {
        updateData({ idVerificationState: st });
        setPhase('success');
        return;
      }
    }
    if (!pollAbort.current) registerFailedAttempt('retry');
  };

  // On mount: resume from hydrated state if possible, else initiate.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const st = sessionData?.idVerificationState;
      if (st === 'verified' || st === 'in_review') { setPhase('success'); return; }
      if (st === 'qr_ready' && sessionData?.idVerificationQrUrl) {
        setQrUrl(sessionData.idVerificationQrUrl);
        setQrLink(sessionData.idVerificationLink || '');
        setPhase('qr');
        return;
      }
      if (initiatedRef.current) return;
      initiatedRef.current = true;
      if (!cancelled) await startInitiate();
    })();
    return () => { cancelled = true; pollAbort.current = true; };
  }, []); // mount-only: run the initiate flow once

  // After MAX_VERIFY_ATTEMPTS failures we land on 'autocontinue': show a brief
  // reassurance, then advance automatically so the seller is never trapped.
  useEffect(() => {
    if (phase !== 'autocontinue') return undefined;
    const t = setTimeout(() => { advance(); }, 2200);
    return () => clearTimeout(t);
  }, [phase]);

  const ShieldCheck = () => (
    <svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
      <path d="M12 2 L20 5 V11 C20 16 16.5 19.5 12 22 C7.5 19.5 4 16 4 11 V5 Z" fill="none" stroke="#27500A" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M8.5 12 L11 14.5 L15.5 9.5" fill="none" stroke="#27500A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const Cross = () => (
    <svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
      <path d="M6 6 L18 18 M18 6 L6 18" fill="none" stroke="#BA7517" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
  const Spinner = ({ heading, sub }) => (
    <StepLayout title={null} hideTrustpilot>
      <style>{`@keyframes idvspin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ textAlign: 'center', padding: '48px 0' }}>
        <div style={{
          width: 48, height: 48, border: '4px solid #ffffff',
          borderTopColor: 'var(--primary)', borderRadius: '50%',
          animation: 'idvspin 1s linear infinite', margin: '0 auto 24px',
        }} />
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{heading}</p>
        <p style={{ color: 'var(--text2)', fontSize: 13 }}>{sub}</p>
        {/* B-IDBADGE-NONBLOCK: even the spinner states have a forward path so a
            slow/failing backend can never strand the seller (the polls also
            time out to a screen with Continue, but never make them wait). */}
        <button type="button" onClick={advance} className="badge-skip-link" style={{ marginTop: 20 }}>
          Skip, I&rsquo;ll do this later
        </button>
      </div>
    </StepLayout>
  );

  if (phase === 'loading') {
    return <Spinner heading="Setting up your verification…" sub="This can take up to a minute. Please keep this page open." />;
  }
  if (phase === 'checking') {
    return <Spinner heading="Confirming your verification…" sub="One moment, checking with LinkedIn." />;
  }

  // Reached the attempt cap — reassure briefly, then auto-advance (effect above).
  if (phase === 'autocontinue') {
    return (
      <StepLayout title={null} hideTrustpilot>
        <div className="waiting-for-you" role="status" aria-live="polite">
          <div className="wfy-pulse" aria-hidden="true">
            <span className="wfy-pulse-core"><ShieldCheck /></span>
          </div>
          <h1 className="wfy-heading">No problem</h1>
          <p className="wfy-instruction">
            We&rsquo;ll sort your verified badge out later, no need to keep trying now.
            Taking you to the next step…
          </p>
          <button type="button" onClick={advance} className="primary-cta wfy-confirm">Continue now</button>
        </div>
      </StepLayout>
    );
  }

  if (phase === 'success') {
    return (
      <StepLayout title={null} hideTrustpilot>
        <div className="waiting-for-you" role="status" aria-live="polite">
          <div className="wfy-pulse" aria-hidden="true">
            <span className="wfy-pulse-core"><ShieldCheck /></span>
          </div>
          <h1 className="wfy-heading">Verification submitted</h1>
          <p className="wfy-instruction">
            Your identity verification is under review by LinkedIn. We&rsquo;ll add your {symbol}5 bonus once your badge is confirmed.
          </p>
          <button type="button" onClick={advance} className="primary-cta wfy-confirm">Continue</button>
        </div>
      </StepLayout>
    );
  }

  if (phase === 'retry' || phase === 'failed') {
    const isFailed = phase === 'failed';
    return (
      <StepLayout title={null} hideTrustpilot>
        <div className="waiting-for-you" role="status" aria-live="polite">
          <div className="wfy-nf-icon" aria-hidden="true"><span className="wfy-nf-core"><Cross /></span></div>
          <h1 className="wfy-heading">
            {isFailed ? 'We hit a snag' : 'We couldn’t confirm it yet'}
          </h1>
          <p className="wfy-instruction wfy-nf-sub">
            {isFailed
              ? (errorMsg || 'We couldn’t open the verification screen just now.')
              : 'If you’ve finished on your phone it can take a moment to register. You can check again, or re-open the code.'}
          </p>

          {isFailed ? (
            <button type="button" onClick={startInitiate} className="primary-cta wfy-confirm">Try again</button>
          ) : (
            <button type="button" onClick={runCheck} className="primary-cta wfy-confirm">Check again</button>
          )}

          {!isFailed && qrUrl && (
            <button type="button" onClick={() => setPhase('qr')} className="badge-skip-link">
              Show my code again
            </button>
          )}

          <button type="button" onClick={advance} className="badge-skip-link">
            Continue
          </button>

          <p className="wfy-disclaimer">
            We&rsquo;ll verify your badge on LinkedIn and add your {symbol}5 bonus once it&rsquo;s confirmed.
          </p>
        </div>
      </StepLayout>
    );
  }

  // phase === 'qr'
  return (
    <StepLayout title="Verify your identity" hideTrustpilot>
      <div className="waiting-for-you">
        {/* LinkedIn's own Persona screen states "The LinkedIn App is required"
            (confirmed live 2026-06-24). Sellers without it tap "I've completed
            it" prematurely and loop, so surface the requirement up front. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          background: '#eef3f8', border: '1px solid #cfe0f0', borderRadius: 12,
          padding: '12px 14px', margin: '4px auto 14px', maxWidth: 380,
          textAlign: 'left', fontSize: 13.5, lineHeight: 1.4,
        }}>
          <span aria-hidden="true" style={{ fontSize: 22 }}>📱</span>
          <span>
            You&rsquo;ll need the <strong>LinkedIn app</strong> installed on your phone,
            get it from the App Store or Google Play before you start.
          </span>
        </div>

        <ol style={{
          textAlign: 'left', maxWidth: 380, margin: '0 auto 16px', padding: '0 0 0 22px',
          fontSize: 14, lineHeight: 1.6, color: 'var(--text2, #555)',
        }}>
          <li>Make sure the <strong>LinkedIn app</strong> is installed on your phone.</li>
          <li>Scan this code below with your phone&rsquo;s camera, or tap <strong>Open verification link</strong>.</li>
          <li>Follow the steps to verify your identity with your ID or passport (about 2 minutes).</li>
          <li>Come back here and tap <strong>&ldquo;I&rsquo;ve completed it&rdquo;</strong>.</li>
        </ol>

        {qrUrl ? (
          <div style={{
            background: '#fff', border: '1px solid var(--border, #e3e3e3)', borderRadius: 16,
            padding: 16, width: 240, maxWidth: '80%', margin: '12px auto 8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
          }}>
            <img
              src={qrUrl}
              alt="Scan this QR code with your phone to verify your identity"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8 }}
            />
          </div>
        ) : (
          <p className="form-field__error">The code didn&rsquo;t load. Tap “Get a new code”.</p>
        )}

        {/* On a phone you can't scan your own screen — the decoded tap-through
            link (from the QR, server-side) opens the verification directly. */}
        {qrLink ? (
          <a
            href={qrLink} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'inline-block', margin: '6px auto 2px', padding: '10px 18px',
              border: '1.5px solid var(--primary, #d2691e)', borderRadius: 10,
              color: 'var(--primary, #d2691e)', fontWeight: 600, fontSize: 14,
              textDecoration: 'none',
            }}
          >
            On your phone? Open verification link →
          </a>
        ) : null}

        <p className="wfy-disclaimer" style={{ marginTop: 8 }}>
          The code expires after a few minutes. Tap &ldquo;Get a new code&rdquo; if it stops working.
        </p>

        {errorMsg && <p className="form-field__error">{errorMsg}</p>}

        <button type="button" onClick={runCheck} className="primary-cta wfy-confirm">
          <span className="wfy-confirm-check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M5 12 L10 17 L19 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          I&rsquo;ve completed it
        </button>

        {/* Refresh the QR + tap-through link (both expire after ~5 min) — re-initiates. */}
        <button type="button" onClick={startInitiate} className="badge-skip-link">
          Get a new code
        </button>

        {/* B-IDBADGE-NONBLOCK: always-present escape. */}
        <button type="button" onClick={advance} className="badge-skip-link">
          Skip for now, I&rsquo;ll add it later
        </button>

        <p className="wfy-disclaimer">
          We&rsquo;ll verify it on LinkedIn before crediting your {symbol}5 bonus
        </p>
      </div>
    </StepLayout>
  );
}


// B-IDBADGE-V2: pick the implementation by flag. Automated QR flow only when
// BOTH ID_BADGE_ENABLED and ID_VERIFICATION_AUTOMATED are on; otherwise the
// legacy "add it yourself" screen (which also owns the !ID_BADGE_ENABLED
// self-skip + its regression tests).
export default function Step12IDBadgeVerification(props) {
  if (ID_BADGE_ENABLED && ID_VERIFICATION_AUTOMATED) {
    return <Step12Automated {...props} />;
  }
  return <Step12Legacy {...props} />;
}
